import React, { lazy, Suspense } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { AuthGuard } from "./AuthGuard";
import { VueBridgeLayout } from "../components/VueBridgeLayout";
import { requiresAuthForRoutePath } from "../../router/authPolicy";

const GamePage = lazy(() => import("../pages/GamePage"));
const OnlinePokerPage = lazy(() => import("../pages/OnlinePokerPage"));
const SettingsPage = lazy(() => import("../pages/SettingsPage"));
const DocumentViewerPage = lazy(() => import("../pages/DocumentViewerPage"));
const DocumentsIndexPage = lazy(() => import("../pages/DocumentsIndexPage"));
const PublicSolvePage = lazy(() => import("../pages/PublicSolvePage"));
const CardMnemonicPage = lazy(() => import("../pages/CardMnemonicPage"));

const publicSolveRequiresAuth = requiresAuthForRoutePath("/solve/:token");
const settingsRequiresAuth = requiresAuthForRoutePath("/settings");

function LoadingFallback() {
  return (
    <div
      data-testid="page-loading"
      className="flex items-center justify-center min-h-screen"
    >
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
    </div>
  );
}

function LegacyPreflopTraceRedirect() {
  const location = useLocation();
  return (
    <Navigate to={`/preflop-trace${location.search}${location.hash}`} replace />
  );
}

/**
 * 메인 React Router 구성.
 *
 * Vue/React hybrid 마이그레이션 단계에서는 아직 마이그레이션되지 않은 페이지
 * (solver, results, history, preflop-tree)를 VueBridgeLayout으로 감싼
 * 단일 영속 VueAppBridge를 통해 렌더링한다.
 * 모든 Vue-bridged route는 같은 layout element를 공유하므로 Vue
 * 앱 인스턴스와 Pinia store가 navigation 사이에 보존된다.
 * 완전히 마이그레이션된 페이지(settings)는 native React 컴포넌트를 사용한다.
 */
export function AppRouter() {
  return (
    <BrowserRouter>
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          {/*
           * Vue-bridged route — 모두 단일 VueBridgeLayout element를 공유한다.
           * React Router는 같은 부모 아래의 sibling route 사이를 이동할 때
           * layout component를 mount 상태로 유지해 Vue 앱 인스턴스와
           * Pinia store 상태를 보존한다.
           */}
          {/*
           * 모든 route는 단일 VueBridgeLayout을 공유해 페이지 navigation 사이에
           * Vue 앱 인스턴스와 Pinia store를 보존한다.
           * React-native 페이지를 밖으로 옮기면 VueBridgeLayout이 unmount되어
           * Pinia 상태(AI Setup config, solver results 등)가 사라진다.
           */}
          <Route element={<VueBridgeLayout />}>
            <Route path="/" element={null} />
            <Route path="/solver" element={null} />
            <Route path="/results" element={null} />
            <Route path="/results/c/:configHash" element={null} />
            <Route path="/history" element={null} />
            <Route path="/preflop-solutions" element={null} />
            <Route path="/easy-preflop-solutions" element={null} />
            <Route path="/preflop-trace" element={null} />
            <Route
              path="/preflop-trace-2"
              element={<LegacyPreflopTraceRedirect />}
            />
            <Route path="/ai-setup" element={null} />
            <Route path="/auth/error" element={null} />
            <Route path="/image-relay/connect" element={null} />

            {/* React-native 페이지 — Pinia 보존을 위해 VueBridgeLayout 내부에 둔다 */}
            <Route
              path="/solve/:token"
              element={
                <AuthGuard requireAuth={publicSolveRequiresAuth}>
                  <Suspense fallback={<LoadingFallback />}>
                    <PublicSolvePage />
                  </Suspense>
                </AuthGuard>
              }
            />

            <Route
              path="/game"
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <GamePage />
                </Suspense>
              }
            />

            <Route
              path="/play"
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <OnlinePokerPage />
                </Suspense>
              }
            />

            <Route
              path="/card-mnemonic"
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <CardMnemonicPage />
                </Suspense>
              }
            />

            <Route
              path="/settings"
              element={
                <AuthGuard requireAuth={settingsRequiresAuth}>
                  <Suspense fallback={<LoadingFallback />}>
                    <SettingsPage />
                  </Suspense>
                </AuthGuard>
              }
            />

            <Route
              path="/documents"
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <DocumentsIndexPage />
                </Suspense>
              }
            />

            <Route
              path="/documents/preflop-walkthrough"
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <DocumentViewerPage slug="preflop-walkthrough" />
                </Suspense>
              }
            />

            <Route
              path="/documents/postflop-code-flow"
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <DocumentViewerPage slug="postflop-code-flow" />
                </Suspense>
              }
            />

            <Route
              path="/documents/regret-sync-strategies"
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <DocumentViewerPage slug="regret-sync-strategies" />
                </Suspense>
              }
            />
          </Route>

          {/* Fallback: 알 수 없는 route를 홈으로 redirect한다 */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
